import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { FolderArchive, Send, Repeat, CheckCircle2, AlertTriangle, XCircle, Clock } from "lucide-react";

export const STATO_ATTIVITA = {
  ok: { cls: "bg-green-100 text-green-800", label: "In regola", Icon: CheckCircle2 },
  in_scadenza: { cls: "bg-amber-100 text-amber-800", label: "In scadenza", Icon: AlertTriangle },
  scaduto: { cls: "bg-red-100 text-red-700", label: "Scaduto", Icon: XCircle },
  senza_scadenza: { cls: "bg-slate-100 text-slate-600", label: "Senza scadenza", Icon: Clock },
};

export const FornitoreFascicoloDialog = ({ fornitore, onClose }) => {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (fornitore) {
      api.get(`/ras/fornitore/${fornitore.id}/attivita`).then((r) => setRows(r.data.attivita)).catch(() => setRows([]));
    } else setRows(null);
  }, [fornitore]);

  const invia = async (doc) => {
    try {
      const { data } = await api.post(`/ras/documento/${doc.id}/richiedi-fornitore`, { fornitore_id: fornitore.id, messaggio: "" });
      toast.success(`Richiesta inviata a ${data.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Invio non riuscito"); }
  };

  return (
    <Dialog open={!!fornitore} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="forn-fascicolo-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <FolderArchive className="w-5 h-5 text-brand" /> Fascicolo documentale · {fornitore?.ragione_sociale}
          </DialogTitle>
        </DialogHeader>
        {!rows && <div className="text-slate-500">Caricamento…</div>}
        {rows && rows.length === 0 && (
          <p className="text-slate-500 text-sm" data-testid="forn-fascicolo-vuoto">
            Nessuna attività periodica assegnata a questo fornitore. Assegnalo come "Fornitore incaricato" su un
            documento ricorrente nella sezione RAS / Fascicolo.
          </p>
        )}
        <div className="space-y-2">
          {(rows || []).map((d) => {
            const st = STATO_ATTIVITA[d.stato] || STATO_ATTIVITA.senza_scadenza;
            return (
              <div key={d.id} className="p-3 rounded-lg border border-slate-200" data-testid={`fascicolo-att-${d.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm break-words">{d.documento}</div>
                    <div className="text-xs text-slate-500">{d.condominio_nome}</div>
                  </div>
                  <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded ${st.cls}`}>
                    <st.Icon className="w-3.5 h-3.5" /> {st.label}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-slate-600">
                  {d.ricorrenza_mesi ? <span className="inline-flex items-center gap-1"><Repeat className="w-3 h-3" /> ogni {d.ricorrenza_mesi} mesi</span> : null}
                  {d.data_scadenza && <span>Scadenza: {d.data_scadenza}</span>}
                  {d.solleciti_count > 0 && <span className="text-red-600 font-bold">{d.solleciti_count} sollecit{d.solleciti_count === 1 ? "o" : "i"} inviat{d.solleciti_count === 1 ? "o" : "i"}</span>}
                  {d.file_name && <span className="text-green-700 break-all">📎 {d.file_name}</span>}
                  <Button size="sm" variant="outline" className="h-7 px-2 text-xs ml-auto" onClick={() => invia(d)} data-testid={`fascicolo-invia-${d.id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Invia richiesta
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
